import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box,
  Center,
  Loader,
  Paper,
  Stack,
  Text,
  Group,
  Button,
  Modal,
  Drawer,
  ActionIcon,
  Indicator,
  Affix,
  Transition,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconAlertCircle, IconArrowLeft, IconMessage, IconX } from "@tabler/icons-react";

import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import {
  tryGetQuestionnaireId,
  asCollaborationRoleString,
  getQuestionnaireLabel,
  type QuestionnaireId,
} from "@/utils/questionnaireMapping";
import type {
  Questionnaire,
  Question,
  Answer,
  MultiSelectAnswer,
  ScaleAnswer,
  PolicyAnswer,
  FullGradingResult,
} from "@/utils/gradingEngine.types";
import { gradeFullQuestionnaire } from "@/utils/gradingEngine";

import q1 from "@/data/questionnaires/questionnaire_1.json";
import q2 from "@/data/questionnaires/questionnaire_2.json";
import q3 from "@/data/questionnaires/questionnaire_3.json";
import q4 from "@/data/questionnaires/questionnaire_4.json";
import q5 from "@/data/questionnaires/questionnaire_5.json";
import q6 from "@/data/questionnaires/questionnaire_6.json";

import AssessmentSectionsNav, { type SectionStat } from "./AssessmentSectionsNav";
import AssessmentHeader from "./AssessmentHeader";
import AssessmentQuestionCard from "./AssessmentQuestionCard";
import AssessmentProgressPanel from "./AssessmentProgressPanel";
import AssessmentChatSidebar from "./AssessmentChatSidebar";
import AssessmentResultsView from "./AssessmentResultsView";

const QUESTIONNAIRE_MAP: Record<string, Questionnaire> = {
  [q1.id]: q1 as unknown as Questionnaire,
  [q2.id]: q2 as unknown as Questionnaire,
  [q3.id]: q3 as unknown as Questionnaire,
  [q4.id]: q4 as unknown as Questionnaire,
  [q5.id]: q5 as unknown as Questionnaire,
  [q6.id]: q6 as unknown as Questionnaire,
};

function isQuestionnaireId(value: unknown): value is QuestionnaireId {
  if (typeof value !== "string") return false;
  return value in QUESTIONNAIRE_MAP;
}

function isAnswered(question: Question, answer: Answer | undefined): boolean {
  if (!answer) return false;
  if (question.type === "multi_select") {
    return ((answer as MultiSelectAnswer).selectedOptionIds?.length ?? 0) > 0;
  }
  return !!(answer as ScaleAnswer | PolicyAnswer).selectedOptionId;
}

type QuestionnaireResponseData = {
  questionnaireId: string | null;
  myAnswers: Record<string, Answer> | null;
  mySubmittedAt: string | null;
  partnerSubmittedAt: string | null;
  bothSubmitted: boolean;
  partnerAnswers: Record<string, Answer> | null;
  gradingResult: unknown | null;
};

type Props = {
  matchId: string;
  collaboration: {
    id: string;
    requesterRole?: string | null;
    expectedPartnerRole?: string | null;
    partnerReadinessQuestionnaireId?: string | null;
    expressInterestTitle?: string | null;
    expressInterestSummary?: string | null;
    requesterCompanyId: string;
    receiverCompanyId: string;
  };
  userCompanyId: string;
  currentUserId: string | undefined;
  /** When false, chat & socket disabled and a "preview locked" UI is shown. */
  chatOpen: boolean;
  /** True when collaboration period has ended; everything becomes read-only. */
  collaborationReadOnly?: boolean;
  /** When true, the workspace is locked (pre-payment). Chat shows preview lock; questionnaire still renders but read-only. */
  workspaceLocked?: boolean;
  /** If the assessment URL ?tab=assessment was the entry, optionally auto-focus first unanswered. */
  autoFocusFirstUnanswered?: boolean;
};

export default function AssessmentTab({
  matchId,
  collaboration,
  userCompanyId,
  currentUserId,
  chatOpen,
  collaborationReadOnly = false,
  workspaceLocked = false,
  autoFocusFirstUnanswered = false,
}: Props) {
  const isLgUp = useMediaQuery("(min-width: 1280px)", false, { getInitialValueInEffect: true });
  const isMdUp = useMediaQuery("(min-width: 900px)", false, { getInitialValueInEffect: true });

  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [loading, setLoading] = useState(true);
  const [responseData, setResponseData] = useState<QuestionnaireResponseData | null>(null);
  const [gradingResult, setGradingResult] = useState<FullGradingResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [chatDrawerOpen, setChatDrawerOpen] = useState(false);
  const gradingTriggered = useRef(false);

  // ── Resolve questionnaire ────────────────────────────────────────────────
  const resolvedQuestionnaireId = useMemo<QuestionnaireId | null>(() => {
    const fromGetById = collaboration.partnerReadinessQuestionnaireId;
    if (isQuestionnaireId(fromGetById)) return fromGetById;
    const fromRoles = tryGetQuestionnaireId(collaboration.requesterRole, collaboration.expectedPartnerRole);
    if (fromRoles) return fromRoles;
    const serverQid = responseData?.questionnaireId;
    return isQuestionnaireId(serverQid) ? serverQid : null;
  }, [
    collaboration.partnerReadinessQuestionnaireId,
    collaboration.requesterRole,
    collaboration.expectedPartnerRole,
    responseData?.questionnaireId,
  ]);
  const questionnaire = resolvedQuestionnaireId ? QUESTIONNAIRE_MAP[resolvedQuestionnaireId] : null;

  // ── Fetch responses ──────────────────────────────────────────────────────
  const fetchResponses = useCallback(async () => {
    setLoading(true);
    try {
      const data = await trpc.collaboration.getQuestionnaireResponses.query({ matchId });
      setResponseData(data as QuestionnaireResponseData);
      if (data.myAnswers) {
        setAnswers(data.myAnswers as unknown as Record<string, Answer>);
      }
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    fetchResponses();
  }, [fetchResponses]);

  // ── Grade when both submitted ────────────────────────────────────────────
  useEffect(() => {
    if (
      !responseData?.bothSubmitted ||
      !responseData?.myAnswers ||
      !responseData?.partnerAnswers ||
      !questionnaire ||
      gradingTriggered.current
    ) {
      return;
    }
    if (responseData.gradingResult) {
      setGradingResult(responseData.gradingResult as FullGradingResult);
      return;
    }
    gradingTriggered.current = true;
    const isRequester = collaboration.requesterCompanyId === userCompanyId;
    const answersA = isRequester
      ? (responseData.myAnswers as Record<string, Answer>)
      : (responseData.partnerAnswers as Record<string, Answer>);
    const answersB = isRequester
      ? (responseData.partnerAnswers as Record<string, Answer>)
      : (responseData.myAnswers as Record<string, Answer>);
    try {
      const result = gradeFullQuestionnaire(questionnaire, answersA, answersB);
      setGradingResult(result);
      if (!collaborationReadOnly) {
        trpc.collaboration.saveGradingResult
          .mutate({ matchId, gradingResult: result as unknown as Record<string, unknown> })
          .catch((err) => console.error("Failed to save grading result:", err));
      }
    } catch (err) {
      console.error("Grading engine error:", err);
    }
  }, [responseData, questionnaire, matchId, collaboration.requesterCompanyId, userCompanyId, collaborationReadOnly]);

  // ── Flatten sections to question list ────────────────────────────────────
  const sections = useMemo(() => questionnaire?.sections ?? [], [questionnaire]);
  const flatQuestions = useMemo(() => {
    const list: Array<{ q: Question; sectionId: string; sectionTitle: string; sectionIndex: number }> = [];
    sections.forEach((s, sIdx) => {
      s.questions.forEach((q) => list.push({ q, sectionId: s.id, sectionTitle: s.title, sectionIndex: sIdx }));
    });
    return list;
  }, [sections]);

  const totalQuestionCount = flatQuestions.length;

  // ── Auto-focus first unanswered question on first load ───────────────────
  const didInitialFocus = useRef(false);
  useEffect(() => {
    if (didInitialFocus.current) return;
    if (loading || !responseData || flatQuestions.length === 0) return;
    if (!autoFocusFirstUnanswered && (responseData.myAnswers ?? Object.keys(answers).length) === 0) {
      // No auto-focus requested and there are no saved answers — start at 0.
      didInitialFocus.current = true;
      setCurrentQuestionIndex(0);
      return;
    }
    const firstUnansweredIdx = flatQuestions.findIndex(({ q }) => !isAnswered(q, answers[q.id]));
    setCurrentQuestionIndex(firstUnansweredIdx === -1 ? 0 : firstUnansweredIdx);
    didInitialFocus.current = true;
  }, [loading, responseData, flatQuestions, answers, autoFocusFirstUnanswered]);

  // ── Derived stats ────────────────────────────────────────────────────────
  const answeredCount = useMemo(
    () => flatQuestions.reduce((n, { q }) => (isAnswered(q, answers[q.id]) ? n + 1 : n), 0),
    [flatQuestions, answers]
  );
  const remainingCount = Math.max(0, totalQuestionCount - answeredCount);
  const percentComplete =
    totalQuestionCount > 0 ? Math.round((answeredCount / totalQuestionCount) * 100) : 0;
  const sectionStats: SectionStat[] = useMemo(() => {
    return sections.map((section, idx) => {
      const total = section.questions.length;
      const done = section.questions.filter((q) => isAnswered(q, answers[q.id])).length;
      const pct = total > 0 ? Math.round((done / total) * 100) : 0;
      const currentSection = flatQuestions[currentQuestionIndex]?.sectionIndex;
      return {
        id: section.id,
        title: section.title,
        percent: pct,
        active: currentSection === idx,
        completed: total > 0 && done === total,
      };
    });
  }, [sections, answers, flatQuestions, currentQuestionIndex]);

  const requiredQuestionIds = useMemo(
    () => flatQuestions.filter(({ q }) => q.required).map(({ q }) => q.id),
    [flatQuestions]
  );
  const requiredSatisfied = useMemo(
    () =>
      requiredQuestionIds.length === 0 ||
      requiredQuestionIds.every((id) => {
        const entry = flatQuestions.find((e) => e.q.id === id);
        return entry ? isAnswered(entry.q, answers[id]) : false;
      }),
    [requiredQuestionIds, flatQuestions, answers]
  );
  const allAnswered = answeredCount === totalQuestionCount && totalQuestionCount > 0;

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleAnswerChange = useCallback((answer: Answer) => {
    setAnswers((prev) => ({ ...prev, [answer.questionId]: answer }));
  }, []);

  const handleClear = useCallback(() => {
    const current = flatQuestions[currentQuestionIndex]?.q;
    if (!current) return;
    setAnswers((prev) => {
      const next = { ...prev };
      delete next[current.id];
      return next;
    });
  }, [flatQuestions, currentQuestionIndex]);

  const handlePrev = useCallback(() => {
    setCurrentQuestionIndex((i) => Math.max(0, i - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentQuestionIndex((i) => Math.min(flatQuestions.length - 1, i + 1));
  }, [flatQuestions.length]);

  const handleSelectSection = useCallback(
    (sectionIndex: number) => {
      const targetIdx = flatQuestions.findIndex((e) => e.sectionIndex === sectionIndex);
      if (targetIdx >= 0) setCurrentQuestionIndex(targetIdx);
    },
    [flatQuestions]
  );

  const performSubmit = useCallback(async () => {
    if (collaborationReadOnly || workspaceLocked || !questionnaire || !requiredSatisfied || !resolvedQuestionnaireId) return;
    setSubmitConfirmOpen(false);
    setSubmitting(true);
    try {
      await trpc.collaboration.submitQuestionnaire.mutate({
        matchId,
        questionnaireId: resolvedQuestionnaireId,
        answers,
      });
      successNotification({
        title: "Assessment Submitted",
        message: "Your responses have been recorded successfully.",
      });
      await fetchResponses();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSubmitting(false);
    }
  }, [
    collaborationReadOnly,
    workspaceLocked,
    questionnaire,
    requiredSatisfied,
    resolvedQuestionnaireId,
    matchId,
    answers,
    fetchResponses,
  ]);

  const requestSubmit = useCallback(() => {
    if (collaborationReadOnly || workspaceLocked || !questionnaire || !requiredSatisfied || !resolvedQuestionnaireId) return;
    if (allAnswered) {
      void performSubmit();
    } else {
      setSubmitConfirmOpen(true);
    }
  }, [
    collaborationReadOnly,
    workspaceLocked,
    questionnaire,
    requiredSatisfied,
    resolvedQuestionnaireId,
    allAnswered,
    performSubmit,
  ]);

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Center mih="50vh">
        <Loader size="lg" color="primary" />
      </Center>
    );
  }

  // Stale collaboration object after route change
  if (collaboration.id !== matchId) {
    return (
      <Center mih="50vh">
        <Loader size="lg" color="primary" />
      </Center>
    );
  }

  // ── No questionnaire resolvable ──────────────────────────────────────────
  if (!resolvedQuestionnaireId || !questionnaire) {
    const hasBothRoles = !!(
      asCollaborationRoleString(collaboration.requesterRole) &&
      asCollaborationRoleString(collaboration.expectedPartnerRole)
    );
    const rolesMapToQuestionnaire =
      hasBothRoles && tryGetQuestionnaireId(collaboration.requesterRole, collaboration.expectedPartnerRole) !== null;
    const emptyDetail = hasBothRoles && !rolesMapToQuestionnaire
      ? "The collaboration roles stored on this match do not map to an Assessment. Please contact support with this collaboration ID."
      : "Collaboration roles have not been assigned yet. The assessment will appear once both parties' roles are determined.";
    return (
      <Center mih="50vh" px="md">
        <Paper
          withBorder
          radius="md"
          p="xl"
          maw={480}
          style={{ backgroundColor: "#ffffff", borderColor: "var(--mantine-color-slate-2)" }}
        >
          <Stack align="center" gap="md">
            <IconAlertCircle size={48} color="var(--mantine-color-primary-6)" stroke={1.5} />
            <Text fw={700} fz="lg" c="primary.8" ta="center">
              No assessment available
            </Text>
            <Text fz="sm" c="slate.6" ta="center" lh={1.55}>
              {emptyDetail}
            </Text>
          </Stack>
        </Paper>
      </Center>
    );
  }

  // ── State variables ──────────────────────────────────────────────────────
  const isSubmitted = !!responseData?.mySubmittedAt;
  const partnerSubmitted = !!responseData?.partnerSubmittedAt;
  const bothSubmitted = !!responseData?.bothSubmitted;
  const effectiveReadOnly = collaborationReadOnly || workspaceLocked || isSubmitted;
  const currentEntry = flatQuestions[currentQuestionIndex];
  const currentAnswer = currentEntry ? answers[currentEntry.q.id] : undefined;

  // ── Middle column content based on state ─────────────────────────────────
  let middleContent: React.ReactNode;
  if (bothSubmitted) {
    middleContent = (
      <AssessmentResultsView
        questionnaire={questionnaire}
        resolvedQuestionnaireId={resolvedQuestionnaireId}
        gradingResult={gradingResult}
        myAnswers={(responseData?.myAnswers as Record<string, Answer> | null) ?? {}}
        partnerAnswers={(responseData?.partnerAnswers as Record<string, Answer> | null) ?? {}}
      />
    );
  } else if (isSubmitted && !partnerSubmitted) {
    middleContent = (
      <Stack gap="md">
        <AssessmentHeader
          title={getQuestionnaireLabel(resolvedQuestionnaireId) || questionnaire.title}
          description={questionnaire.description}
          sectionIndex={currentEntry?.sectionIndex ?? 0}
          totalSections={sections.length}
          percentComplete={100}
        />
        <Paper
          withBorder
          radius="md"
          p="xl"
          style={{ backgroundColor: "#ffffff", borderColor: "var(--mantine-color-slate-2)" }}
        >
          <Center>
            <Stack align="center" gap="md">
              <Text fw={700} fz="lg" c="primary.8">
                Waiting for your partner
              </Text>
              <Text fz="sm" c="slate.6" ta="center" maw={420}>
                Your responses have been recorded. Compatibility results will appear here once your partner submits
                their assessment.
              </Text>
            </Stack>
          </Center>
        </Paper>
      </Stack>
    );
  } else {
    middleContent = (
      <Stack gap="md">
        <AssessmentHeader
          title={getQuestionnaireLabel(resolvedQuestionnaireId) || questionnaire.title}
          description={questionnaire.description}
          sectionIndex={currentEntry?.sectionIndex ?? 0}
          totalSections={sections.length}
          percentComplete={percentComplete}
        />
        {currentEntry && (
          <AssessmentQuestionCard
            question={currentEntry.q}
            answer={currentAnswer}
            questionIndex={currentQuestionIndex}
            totalQuestions={totalQuestionCount}
            sectionTitle={currentEntry.sectionTitle}
            isFirst={currentQuestionIndex === 0}
            isLast={currentQuestionIndex === totalQuestionCount - 1}
            readOnly={effectiveReadOnly}
            submitting={submitting}
            canSubmit={requiredSatisfied}
            onChange={handleAnswerChange}
            onClear={handleClear}
            onPrev={handlePrev}
            onNext={handleNext}
            onSubmit={requestSubmit}
          />
        )}
      </Stack>
    );
  }

  // ── Sections nav (left) ──────────────────────────────────────────────────
  const sectionsNav = (
    <Box style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, overflowY: "auto" }}>
      <AssessmentSectionsNav sections={sections} stats={sectionStats} onSelect={handleSelectSection} />
      {!bothSubmitted && (
        <AssessmentProgressPanel
          percent={percentComplete}
          answered={answeredCount}
          total={totalQuestionCount}
          remaining={remainingCount}
        />
      )}
    </Box>
  );

  // ── Right column (chat only — fills full height) ─────────────────────────
  const rightColumn = (
    <Box style={{ height: "100%", minHeight: 0, display: "flex", flexDirection: "column" }}>
      <AssessmentChatSidebar
        matchId={matchId}
        chatOpen={chatOpen}
        collaborationReadOnly={collaborationReadOnly}
        currentUserId={currentUserId}
        previewLocked={workspaceLocked}
      />
    </Box>
  );

  // ── Layout ───────────────────────────────────────────────────────────────
  if (isLgUp) {
    return (
      <>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "220px 1fr 360px",
            gap: 16,
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              backgroundColor: "#fafbfc",
              borderRadius: 10,
              border: "1px solid var(--mantine-color-slate-2)",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {sectionsNav}
          </Box>
          <Box style={{ overflowY: "auto", minHeight: 0, paddingRight: 4 }}>{middleContent}</Box>
          <Box style={{ minHeight: 0, display: "flex", flexDirection: "column" }}>{rightColumn}</Box>
        </Box>
        <SubmitConfirmModal
          opened={submitConfirmOpen}
          unansweredCount={remainingCount}
          submitting={submitting}
          onCancel={() => setSubmitConfirmOpen(false)}
          onConfirm={() => void performSubmit()}
        />
      </>
    );
  }

  if (isMdUp) {
    return (
      <>
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "200px 1fr",
            gap: 16,
            flex: 1,
            minHeight: 0,
            height: "100%",
            overflow: "hidden",
          }}
        >
          <Box
            style={{
              backgroundColor: "#fafbfc",
              borderRadius: 10,
              border: "1px solid var(--mantine-color-slate-2)",
              overflow: "hidden",
              minHeight: 0,
            }}
          >
            {sectionsNav}
          </Box>
          <Box style={{ overflowY: "auto", minHeight: 0, paddingRight: 4 }}>{middleContent}</Box>
        </Box>
        <ChatDrawer
          opened={chatDrawerOpen}
          onClose={() => setChatDrawerOpen(false)}
          matchId={matchId}
          chatOpen={chatOpen}
          collaborationReadOnly={collaborationReadOnly}
          currentUserId={currentUserId}
          previewLocked={workspaceLocked}
        />
        <ChatFab
          onClick={() => setChatDrawerOpen(true)}
          disabled={!chatOpen}
          locked={workspaceLocked}
        />
        <SubmitConfirmModal
          opened={submitConfirmOpen}
          unansweredCount={remainingCount}
          submitting={submitting}
          onCancel={() => setSubmitConfirmOpen(false)}
          onConfirm={() => void performSubmit()}
        />
      </>
    );
  }

  // Mobile: single column + chat drawer
  return (
    <>
      <Box style={{ flex: 1, minHeight: 0, overflowY: "auto", paddingBottom: 64 }}>
        <Stack gap="md">
          <Paper
            withBorder
            radius="md"
            p="md"
            style={{ backgroundColor: "#ffffff", borderColor: "var(--mantine-color-slate-2)" }}
          >
            {sectionsNav}
          </Paper>
          {middleContent}
        </Stack>
      </Box>
      <ChatDrawer
        opened={chatDrawerOpen}
        onClose={() => setChatDrawerOpen(false)}
        matchId={matchId}
        chatOpen={chatOpen}
        collaborationReadOnly={collaborationReadOnly}
        currentUserId={currentUserId}
        previewLocked={workspaceLocked}
      />
      <ChatFab onClick={() => setChatDrawerOpen(true)} disabled={!chatOpen} locked={workspaceLocked} />
      <SubmitConfirmModal
        opened={submitConfirmOpen}
        unansweredCount={remainingCount}
        submitting={submitting}
        onCancel={() => setSubmitConfirmOpen(false)}
        onConfirm={() => void performSubmit()}
      />
    </>
  );
}

function SubmitConfirmModal({
  opened,
  unansweredCount,
  submitting,
  onCancel,
  onConfirm,
}: {
  opened: boolean;
  unansweredCount: number;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Modal
      opened={opened}
      onClose={onCancel}
      title={
        <Text fw={700} fz="md" c="primary.8">
          Unanswered questions
        </Text>
      }
      centered
      overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
    >
      <Text fz="sm" c="slate.6" mb="lg" lh={1.55}>
        You still have {unansweredCount} unanswered question{unansweredCount === 1 ? "" : "s"}. Do you want to submit
        anyway?
      </Text>
      <Group justify="flex-end" gap="sm">
        <Button variant="default" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          loading={submitting}
          onClick={onConfirm}
          color="yellow"
          variant="filled"
          styles={{ root: { color: "var(--mantine-color-primary-7)", fontWeight: 700 } }}
        >
          Submit anyway
        </Button>
      </Group>
    </Modal>
  );
}

function ChatDrawer({
  opened,
  onClose,
  matchId,
  chatOpen,
  collaborationReadOnly,
  currentUserId,
  previewLocked,
}: {
  opened: boolean;
  onClose: () => void;
  matchId: string;
  chatOpen: boolean;
  collaborationReadOnly?: boolean;
  currentUserId: string | undefined;
  previewLocked: boolean;
}) {
  const NAVY = "var(--mantine-color-primary-7)";
  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      withCloseButton={false}
      padding={0}
      closeOnEscape
      closeOnClickOutside
      trapFocus
      zIndex={9999}
      lockScroll
      styles={{
        root: { zIndex: 9999 },
        inner: { zIndex: 9999 },
        overlay: { zIndex: 9998 },
        body: { padding: 0, display: "flex", flexDirection: "column", height: "100%", minHeight: 0 },
        content: { display: "flex", flexDirection: "column", backgroundColor: "#ffffff", zIndex: 9999 },
        header: { display: "none" },
      }}
    >
      {/* Sticky back-bar (renders unconditionally because it lives inside Drawer.Body) */}
      <Box
        style={{
          position: "sticky",
          top: 0,
          zIndex: 5,
          backgroundColor: NAVY,
          color: "#ffffff",
          borderBottom: "1px solid rgba(255,255,255,0.12)",
          flexShrink: 0,
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" px="md" py="sm">
          <Group gap="sm" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
            <ActionIcon
              size={40}
              radius="md"
              onClick={onClose}
              aria-label="Back to assessment"
              styles={{
                root: {
                  backgroundColor: "rgba(255,255,255,0.16)",
                  color: "#ffffff",
                  border: "1px solid rgba(255,255,255,0.2)",
                },
              }}
            >
              <IconArrowLeft size={22} stroke={2} />
            </ActionIcon>
            <Box style={{ minWidth: 0 }}>
              <Text fw={700} fz="md" c="white" lh={1.2} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                Collaboration Chat
              </Text>
              <Group gap={6} align="center" wrap="nowrap" mt={2}>
                <Box
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: chatOpen && !previewLocked ? "var(--mantine-color-green-4)" : "var(--mantine-color-yellow-4)",
                  }}
                />
                <Text fz={11} c="white" fw={500} style={{ opacity: 0.9 }}>
                  {chatOpen && !previewLocked ? "Online" : "Locked"}
                </Text>
              </Group>
            </Box>
          </Group>
          <ActionIcon
            size={40}
            radius="md"
            onClick={onClose}
            aria-label="Close chat"
            styles={{
              root: {
                backgroundColor: "transparent",
                color: "#ffffff",
              },
            }}
          >
            <IconX size={24} stroke={2} />
          </ActionIcon>
        </Group>
      </Box>

      <Box style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <AssessmentChatSidebar
          matchId={matchId}
          chatOpen={chatOpen}
          collaborationReadOnly={collaborationReadOnly}
          currentUserId={currentUserId}
          previewLocked={previewLocked}
          hideHeader
        />
      </Box>
    </Drawer>
  );
}

function ChatFab({
  onClick,
  disabled,
  locked,
}: {
  onClick: () => void;
  disabled: boolean;
  locked: boolean;
}) {
  return (
    <Affix position={{ bottom: 20, right: 20 }} zIndex={50}>
      <Transition transition="slide-up" mounted={!disabled || locked}>
        {(styles) => (
          <Indicator
            color={locked ? "yellow" : "green"}
            offset={6}
            size={10}
            disabled={disabled}
            processing={!disabled}
            styles={{ root: { ...styles } }}
          >
            <Button
              radius="xl"
              size="md"
              leftSection={<IconMessage size={18} />}
              onClick={onClick}
              disabled={disabled && !locked}
              styles={{
                root: {
                  backgroundColor: "var(--mantine-color-primary-7)",
                  boxShadow: "0 8px 20px rgba(4, 48, 92, 0.25)",
                },
              }}
            >
              Chat
            </Button>
          </Indicator>
        )}
      </Transition>
    </Affix>
  );
}
